import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { eventTargetNotInTextbox } from 'donor-elf-web/lib/document-utils';

// args
// - dontAddKeydownEventListener: boolean - default false
// - placeholder: string - default "Search Contacts"
// - smallFormControl: boolean - default false

export default class extends Component {
  @service contactsCache;
  @service powerMenu;
  @service router;

  @tracked q = '';
  @tracked highlightedIndex = 0;
  // set when then dropdown is opened
  dropdown = null;
  element = null;

  get filteredContacts() {
    let query = this.q || '';
    // don't search until they have entered 2 letters
    if (query.length < 2) {
      query = '';
    }

    if (/^\d{4,}$/.test(query)) {
      // if more than 4 digits, then assume searching based on contact id
      return this.contactsCache.sortedContacts
        .filter((x) => x.id === query)
        .slice(0, 10);
    } else {
      return this.contactsCache.search(query, { limit: 10 });
    }
  }

  get noContacts() {
    return (this.contactsCache.contacts?.length || 0) === 0;
  }

  get placeholder() {
    return this.args.placeholder || 'Search Contacts';
  }

  get inputClass() {
    return this.args.smallFormControl
      ? 'form-control form-control-sm'
      : 'form-control';
  }

  // whether the dropdown should be opened when the search textbox receives focus.
  // This is set when selecting a contact by clicking the name so that the
  // dropdown doesn't stay open
  dontOpenDropdown = false;

  @action
  handleDocumentKeydown(e) {
    // focus on contact search when the "/" key is pressed
    if (e.which === 191 && eventTargetNotInTextbox(e)) {
      e.preventDefault();
      this.focusOnSearchTextbox();
    }
  }

  focusOnSearchTextbox() {
    this.element.querySelector('input.form-control').focus();
  }

  @action
  templateDidInsert(dropdown, el) {
    this.dropdown = dropdown;
    this.element = el.closest('.navbar-search-contacts');
    if (this.args.dontAddKeydownEventListener !== true) {
      document.addEventListener('keydown', this.handleDocumentKeydown);
    }
    this.router.on('routeWillChange', this, this.closeDropdown);
    this.powerMenu.appendOptionsFn(
      'navbar-search-contacts',
      this.powerMenuOptions,
    );
  }

  @action
  templateWillDestroy() {
    this.router.off('routeWillChange', this, this.closeDropdown);
    document.removeEventListener('keydown', this.handleDocumentKeydown);
  }

  @action
  powerMenuOptions() {
    return [
      {
        label: 'Search Contacts',
        fn: () => {
          this.focusOnSearchTextbox();
          this.dropdown.actions.open();
        },
      },
    ];
  }

  @action
  handleTriggerKeyDown(e) {
    // space key should not close the dropdown
    if (e.which === 32) {
      e.stopImmediatePropagation();
      // enter
    } else if (e.which === 13) {
      e.stopImmediatePropagation();
      let highlightedContact = this.filteredContacts[this.highlightedIndex];
      if (highlightedContact) {
        this.selectContact(highlightedContact);
      }
      // tab key or escape key
    } else if (e.which === 9 || e.which === 27) {
      e.stopImmediatePropagation();
      this.dropdown.actions.close();
    } else if (e.which === 40) {
      // Down arrow
      e.preventDefault();
      if (this.highlightedIndex + 1 < this.filteredContacts.length) {
        this.highlightedIndex += 1;
      }
    } else if (e.which === 38) {
      // Up arrow
      e.preventDefault();
      if (this.highlightedIndex > 0) {
        this.highlightedIndex -= 1;
      }
    }
    return true;
  }

  @action
  handleSearchInput(e) {
    this.q = e.target.value;
    this.highlightedIndex = 0;
    if (this.q.length >= 2) {
      this.dropdown.actions.open();
    } else if (this.q.length === 0) {
      this.dropdown.actions.close();
      setTimeout(() => {
        e.target.focus();
      });
    }
  }

  @action
  handleTriggerClick(e) {
    if (isEmpty(this.q)) {
      // don't open the dropdown on click unless a search term has already been entered
      e.stopImmediatePropagation();
    }
  }

  @action
  handleOnOpen(dropdown) {
    this.dropdown = dropdown;
  }

  @action
  selectContact(contact) {
    this.q = '';
    this.highlightedIndex = 0;
    this.router.transitionTo('funds.fund.contacts.contact', contact.id);
  }

  closeDropdown() {
    if (this.dropdown) {
      this.dontOpenDropdown = true;
      this.dropdown.actions.close();
      this.element.querySelector('.form-control').blur();
      next(() => {
        this.dontOpenDropdown = false;
      });
    }
  }
}
